import { JsxElement, JsxElementType } from "@/utils/jsx/jsxElementTypes";
import { getUnexpiredToken, useCanvasId } from "@/utils/utils";
import { useSession } from "@clerk/clerk-react";
import axios from "axios";
import { isEqual } from "lodash";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  generateStoryControlPlaceholder,
  StoryControlsSpec,
  StoryControlValues,
} from "../RightStyle/StoryControlsManager";

/**
 * Hook that pre-caches an image and provides callbacks for on-demand drag image creation.
 * Unlike the previous implementation, this does NOT subscribe to zoom changes -
 * the drag image is created synchronously at drag time with the current zoom level.
 */
export const useDragImage = (
  imagePath: string | undefined,
  width?: number,
  height?: number,
) => {
  // Pre-cached img element (never added to DOM, just in memory for cloning)
  const imgRef = useRef<HTMLImageElement | null>(null);
  // Wrapper div created on drag, cleaned up on drag end
  const dragDivRef = useRef<HTMLDivElement | null>(null);
  // Track natural dimensions from loaded image
  const [trueDimensions, setTrueDimensions] = useState({ width: 0, height: 0 });

  // Pre-cache the image element when imagePath changes
  useEffect(() => {
    if (!imagePath) {
      imgRef.current = null;
      setTrueDimensions({ width: 0, height: 0 });
      return;
    }

    const img = new Image();
    img.src = imagePath;
    img.onload = () => {
      setTrueDimensions({ width: img.naturalWidth, height: img.naturalHeight });
    };
    imgRef.current = img;

    return () => {
      imgRef.current = null;
    };
  }, [imagePath]);

  // Called synchronously in onDragStart - creates div, sets drag image
  const setupDragImage = useCallback(
    (e: React.DragEvent, zoomPerc: number) => {
      if (!imgRef.current || !imagePath) return;

      // Clean up any existing drag div
      if (dragDivRef.current?.parentNode) {
        dragDivRef.current.parentNode.removeChild(dragDivRef.current);
      }

      const scale = zoomPerc || 1;
      const imgWidth = width ?? (imgRef.current.naturalWidth || 100);
      const imgHeight = height ?? (imgRef.current.naturalHeight || 100);
      const scaledWidth = imgWidth * scale;
      const scaledHeight = imgHeight * scale;

      // Create wrapper div
      const div = document.createElement("div");
      div.className = "tempo-drag-image";
      div.style.width = `${scaledWidth}px`;
      div.style.height = `${scaledHeight}px`;
      div.style.position = "fixed";
      div.style.left = "-9999px";
      div.style.top = "-9999px";
      div.style.pointerEvents = "none";
      div.style.zIndex = "999999";
      div.style.overflow = "hidden";

      // Clone and size the cached img
      const imgClone = imgRef.current.cloneNode() as HTMLImageElement;
      imgClone.style.width = `${scaledWidth}px`;
      imgClone.style.height = `${scaledHeight}px`;
      imgClone.style.objectFit = "contain";
      imgClone.draggable = false;

      div.appendChild(imgClone);
      document.body.appendChild(div);
      dragDivRef.current = div;

      // Set as drag image
      const offsetX = scaledWidth / 2;
      const offsetY = scaledHeight / 2;
      e.dataTransfer.setDragImage(div, offsetX, offsetY);
    },
    [imagePath, width, height],
  );

  // Called in onDragEnd - cleans up the wrapper div
  const cleanupDragImage = useCallback(() => {
    if (dragDivRef.current?.parentNode) {
      dragDivRef.current.parentNode.removeChild(dragDivRef.current);
      dragDivRef.current = null;
    }
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      cleanupDragImage();
    };
  }, [cleanupDragImage]);

  return { setupDragImage, cleanupDragImage, trueDimensions };
};

export const useStoryboardControls = (selectedElement: JsxElement) => {
  const { session } = useSession();
  const canvasId = useCanvasId();

  // Store controls and values as records keyed by storyCodebaseId
  const [controls, setControls] = useState<StoryControlsSpec>({});
  const [localValues, setValues] = useState<StoryControlValues>({});
  const [currentValues, setCurrentValues] = useState<StoryControlValues>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch controls when storyboard changes
  useEffect(() => {
    const fetchControlsSpec = async () => {
      setLoading(true);
      setError(null);
      try {
        const supabaseJWT = await getUnexpiredToken(session);
        const response = await axios.post(
          `${
            import.meta.env.VITE_BACKEND_BASE_URL
          }/canvases/${canvasId}/parseAndMutate/parse/parseStoryControls`,
          { filepath: selectedElement.filename },
          { headers: { Authorization: `Bearer ${supabaseJWT}` } },
        );

        const controlsFromServer: StoryControlsSpec = response.data || {};

        // Add placeholders to controls
        const controlsWithPlaceholders: StoryControlsSpec = {};
        for (const [key, control] of Object.entries(controlsFromServer)) {
          controlsWithPlaceholders[key] = {
            ...control,
            placeholder: generateStoryControlPlaceholder(control),
          };
        }

        if (controlsWithPlaceholders) {
          setControls(controlsWithPlaceholders);
        }
      } catch (e) {
        console.error("Failed to fetch story controls:", e);
        setError("Failed to load story controls");
        // Clear for this storyCodebaseId on error
        setControls({});
        setValues({});
      } finally {
        setLoading(false);
      }
    };

    if (selectedElement.type !== JsxElementType.StorybookComponent) {
      return;
    }

    fetchControlsSpec();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedElement.filename, canvasId]);

  const handleChange = useCallback((changes: StoryControlValues) => {
    setValues((prev) => ({ ...prev, ...changes }));
  }, []);

  // what values are present in values but not present in currentValues or not equal to the value in currentValues
  const changed = useMemo(
    () =>
      Object.keys(localValues).filter((key) => {
        return !currentValues[key] || localValues[key] !== currentValues[key];
      }),
    [localValues, currentValues],
  );

  const values = useMemo(
    () => ({ ...currentValues, ...localValues }),
    [currentValues, localValues],
  );

  const handleSetCurrentValues = useCallback(
    (values: StoryControlValues) =>
      setCurrentValues((old) => {
        if (isEqual(values, old)) {
          return old;
        }
        return values;
      }),
    [],
  );

  return {
    controls,
    values,
    handleSetCurrentValues,
    loading,
    error,
    displayName: selectedElement.componentName,
    changed,
    handleChange,
  } as const;
};
